import type {
  KundaliRequest,
  KundaliResponse,
  DoshaResult,
  PlanetPosition,
  HouseData,
  RashiInfo,
  MatchingRequest,
  MatchingResponse,
  PersonDetails,
  ProductRecommendation,
  RashifalResponse,
  DoshaFinding,
  DoshaCrosscheckResult,
  NavamshaResult,
  MatchingDashaResult,
  DetailedMatchingResponse,
} from "@ishvaram/api-client";
import { getGoogleAnalyticsClientId } from "@/lib/analytics-ga4";
import { getOrCreateBootstrapDistinctId as getAnalyticsDistinctId } from "@/lib/analytics-identity";
import { API_BASE, fetchWithTimeout, throwApiError } from "./_fetch";

export type {
  KundaliRequest,
  KundaliResponse,
  DoshaResult,
  PlanetPosition,
  HouseData,
  RashiInfo,
  MatchingRequest,
  MatchingResponse,
  PersonDetails,
  ProductRecommendation,
  RashifalResponse,
  DoshaFinding,
  DoshaCrosscheckResult,
  NavamshaResult,
  DetailedMatchingResponse,
};
export type { KootaResult, MangalDoshaResult, PersonInput } from "@ishvaram/api-client";

// DashaProblemPeriod / DashaResult (matching context) — re-exported under
// web-local names for backwards compat; MatchingDashaResult has the concrete shape.
export type DashaProblemPeriod = MatchingDashaResult["problem_periods"][number];
export type { MatchingDashaResult as DashaResult };

const MAX_KUNDALI_ATTEMPTS = 3;

/**
 * Handle a non-ok Kundali response within the retry loop.
 * Returns false if the caller should continue to the next attempt,
 * or throws immediately for 4xx / final attempt.
 */
async function handleKundaliErrorResponse(res: Response, attempt: number): Promise<false> {
  // 4xx is a client error: don't retry.
  if (res.status >= 400 && res.status < 500) {
    await throwApiError(res);
  }
  if (attempt < MAX_KUNDALI_ATTEMPTS) {
    await new Promise((r) => setTimeout(r, 500 * attempt));
    return false; // signal: continue
  }
  await throwApiError(res);
  // throwApiError always throws; this satisfies TS return type
  return false;
}

export async function generateKundali(data: KundaliRequest): Promise<KundaliResponse> {
  // 30s timeout per attempt because /kundali does Swiss Ephemeris + Sarvam
  // LLM (18-22s cold-start). Retries on transient network failures: 2026-05
  // funnel data showed conversion dropping from 83% to 0% with the bulk of
  // failures surfacing as native "TypeError: Failed to fetch" from Indian
  // ISPs flaking on direct calls to *.run.app. Three attempts with brief
  // backoff covers the common "first packet drops, retry succeeds" pattern.
  let lastError: unknown = null;
  for (let attempt = 1; attempt <= MAX_KUNDALI_ATTEMPTS; attempt++) {
    try {
      const res = await fetchWithTimeout(
        `${API_BASE}/kundali`,
        {
          method: "POST",
          headers: kundaliAnalyticsHeaders(),
          body: JSON.stringify(data),
        },
        30_000
      );
      if (!res.ok) {
        await handleKundaliErrorResponse(res, attempt);
        continue;
      }
      return res.json();
    } catch (err) {
      lastError = err;
      // Don't retry if the error came from throwApiError (client error already raised)
      if (err instanceof Error && err.name === "ApiError") throw err;
      if (attempt < MAX_KUNDALI_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, 500 * attempt));
        continue;
      }
      // Out of attempts: surface a more informative error than the raw
      // browser "TypeError: Failed to fetch" so triage can distinguish
      // network/cors/timeout from the actual class.
      const cause = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      throw new Error(`Kundali API unreachable after ${MAX_KUNDALI_ATTEMPTS} attempts (${cause})`);
    }
  }
  // Unreachable, satisfies TS
  throw lastError instanceof Error ? lastError : new Error("Kundali API failed");
}

/**
 * Fetch a previously saved reading by id (GET /kundali/{reading_id}) to reuse
 * the birth details the seeker already entered — e.g. the agentic cart
 * prefilling its report form instead of re-asking. Best-effort: callers treat a
 * rejection as "no prefill, the user types it".
 */
export async function getSavedKundali(readingId: string): Promise<KundaliResponse> {
  const res = await fetchWithTimeout(
    `${API_BASE}/kundali/${encodeURIComponent(readingId)}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
    15_000
  );
  if (!res.ok) throw new Error(`Could not load saved reading (${res.status})`);
  return res.json() as Promise<KundaliResponse>;
}

function kundaliAnalyticsHeaders(): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (typeof window === "undefined") return headers;
  const posthogDistinctId = getAnalyticsDistinctId();
  const gaClientId = getGoogleAnalyticsClientId();
  if (posthogDistinctId) headers["X-PostHog-Distinct-Id"] = posthogDistinctId;
  if (gaClientId) headers["X-GA-Client-Id"] = gaClientId;
  headers["X-Ishvaram-Source-Path"] = window.location.pathname || "/free-kundali";
  return headers;
}

export async function getRashifal(
  rashi: string,
  period = "daily",
  language = "en"
): Promise<RashifalResponse> {
  const qp = new URLSearchParams();
  if (period !== "daily") qp.set("period", period);
  if (language !== "en") qp.set("language", language);
  const qs = qp.toString();
  // Monthly/weekly LLM generation takes longer — give them 40s vs the default 12s
  const timeoutMs = period === "monthly" ? 40_000 : period === "weekly" ? 30_000 : 12_000;
  const res = await fetchWithTimeout(
    `${API_BASE}/rashifal/${rashi}${qs ? `?${qs}` : ""}`,
    {},
    timeoutMs
  );
  if (!res.ok) throw new Error(`Failed to fetch rashifal for ${rashi}`);
  return res.json();
}

export async function listRashis(): Promise<RashiInfo[]> {
  const res = await fetchWithTimeout(`${API_BASE}/rashifal`);
  if (!res.ok) throw new Error("Failed to fetch rashis");
  return res.json();
}

export async function calculateMatching(data: MatchingRequest): Promise<MatchingResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/matching`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function calculateDetailedMatching(
  data: MatchingRequest
): Promise<DetailedMatchingResponse> {
  const res = await fetchWithTimeout(`${API_BASE}/matching/detailed`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) await throwApiError(res);
  return res.json();
}

export async function downloadMatchingReport(
  data: MatchingRequest & { language?: string }
): Promise<Blob> {
  const res = await fetchWithTimeout(`${API_BASE}/reports/matching`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      person_a: data.person_a,
      person_b: data.person_b,
      language: data.language || "en",
    }),
  });
  if (!res.ok) await throwApiError(res, "Report generation failed");
  return res.blob();
}
